import {
  CSSProperties,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useTranslations from "../../core/i18n/useTranslations";
import { IDropdownItem, IItemProp } from "../components/Dropdown/DropdownItem";
import { Dictionary } from "lodash";
import { ICheckbox } from "../components/Checkbox/Checkbox";

export type DropdownVariant = "filter" | "form";

export interface DropdownProps {
  items: Array<IItemProp>;
  selectedItem?: IItemProp;
  variant?: DropdownVariant;
  withoutCheckIcon?: boolean;
  withFilter?: boolean;
  onSelectItem(arg0: IDropdownItem, e: any): void;
  onOpenStateChanged?(isOpen: boolean): void;
  title?: string;
  placeholder?: string;
  width?: number | string;
  bodyWidth?: number | string;
  bodyStyle?: CSSProperties;
  className?: string;
  popupClassName?: string;
  buttonStyle?: Dictionary<CSSProperties>;
  dropdownInPopup?: boolean;
  invalid?: boolean;
  validationError?: string;
  disabled?: boolean;
  maxVisible?: number;
  id?: string;
  withClearItem?: boolean;
  innerButtonStyles?: CSSProperties;
  showTooltip?: boolean;
  dataCy?: string;
  isLoading?: boolean;
  searchPlaceholder?: string;
  ariaLable?: string;
  required?: boolean;
}

export interface IDropdownWithCheckboxes {
  items: ICheckbox[];
  id?: string;
  placeholder?: string;
  width?: number | string;
  containerWidth?: number | string;
  getSelectedItems(arg0: ICheckbox[]): void;
  title?: string;
  withFilter?: boolean;
  maxVisible?: number;
  hasError?: boolean;
}

const ScrollBuffer = 8;
const scrollToFocusedOption = (
  optionContainer: RefObject<HTMLDivElement>,
  focusedIndex: number,
) => {
  if (!optionContainer.current || focusedIndex < 0) return;

  const children = optionContainer.current.getElementsByClassName("option");
  const focusedChild = children?.length
    ? (children[focusedIndex] as HTMLDivElement)
    : null;
  if (!focusedChild?.getBoundingClientRect) return;

  const { height: optionHeight } = focusedChild.getBoundingClientRect();
  const { height: listHeight } =
    optionContainer.current.getBoundingClientRect();

  const scrollTop = optionContainer.current.scrollTop;
  const offsetBeforeTop = optionHeight * 2;

  const scrollOffset =
    focusedChild.offsetTop - optionContainer.current.offsetTop;

  const isInView =
    scrollOffset >= scrollTop &&
    scrollOffset + optionHeight <= scrollTop + listHeight;

  const isAbove = scrollOffset < scrollTop + offsetBeforeTop;

  if (isInView) return;

  if (isAbove) {
    optionContainer.current.scrollTop = scrollOffset - offsetBeforeTop;
  } else {
    optionContainer.current.scrollTo({
      top: scrollOffset - listHeight + optionHeight + ScrollBuffer,
    });
  }
};

const useScroll = (
  isOpen: boolean,
  focusedIndex: number,
  optionContainer: RefObject<HTMLDivElement>,
  avoidScroll?: boolean,
  withButtons?: boolean,
) => {
  useEffect(() => {
    if (avoidScroll) return;
    scrollToFocusedOption(optionContainer, focusedIndex);
  }, [
    isOpen,
    focusedIndex,
    optionContainer.current?.scrollHeight,
    withButtons,
  ]);
};

export const useDropdownHooks = (props: DropdownProps) => {
  const t = useTranslations();

  const {
    items: _items,
    onSelectItem,
    onOpenStateChanged,
    selectedItem,
    withFilter = false,
    withoutCheckIcon,
    title,
    buttonStyle = {},
    placeholder,
    variant = "form",
    width,
    bodyWidth,
    bodyStyle,
    className = "",
    popupClassName = "",
    dropdownInPopup = false,
    invalid = false,
    validationError = undefined,
    disabled = false,
    maxVisible,
    id,
    withClearItem,
    innerButtonStyles,
    showTooltip,
    dataCy,
    isLoading,
    searchPlaceholder,
    ariaLable,
    required,
  } = props;
  const [focusedIndex, setFocusedIndex] = useState({
    idx: -1,
    avoidScroll: false,
  });
  const [isOpen, setIsOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropdownFocused, setDropdownFocused] = useState(false);

  const isFilter = variant === "filter";

  // A filter is cleared through its own placeholder, so the clear option is
  // that text wrapped in dashes. Anything else gets a unified label.
  const clearItemName =
    isFilter && placeholder ? `--${placeholder}--` : t("Objects:not_specified");

  const items: IItemProp[] = useMemo(
    () =>
      withClearItem
        ? [
            { id: -1, name: clearItemName, isSelected: !selectedItem },
            ..._items,
          ]
        : _items,
    [withClearItem, clearItemName, _items, selectedItem],
  );

  const selectedIndex = useMemo(
    () => !!items && items.map((o) => o.id).indexOf(selectedItem?.id ?? -1),
    [items, selectedItem],
  );

  const closeDropdown = useCallback(
    (focus = false) => {
      setFocusedIndex({ idx: selectedIndex, avoidScroll: false });
      setIsOpen(false);
      if (focus && btnRef.current) {
        btnRef.current.focus();
      }
    },
    [btnRef.current, isOpen, selectedIndex],
  );

  const openDropdown = useCallback(() => {
    setFocusedIndex({ idx: selectedIndex, avoidScroll: false });
    setIsOpen(true);
  }, [selectedIndex, isOpen]);

  const setValue = useCallback(
    (e: any, newOption?: IDropdownItem, shouldClose = false) => {
      if (newOption) {
        onSelectItem(newOption, e);
      }
      if (shouldClose) {
        closeDropdown(true);
      }
    },
    [onSelectItem, closeDropdown],
  );
  useScroll(isOpen, focusedIndex.idx, container, focusedIndex.avoidScroll);

  return {
    focusedIndex,
    setFocusedIndex,
    onOpenStateChanged,
    isOpen,
    setIsOpen,
    dropdownFocused,
    setDropdownFocused,
    setValue,
    openDropdown,
    closeDropdown,
    container,
    btnRef,
    selectedItem,
    items,
    withFilter,
    withoutCheckIcon,
    onSelectItem,
    title,
    placeholder,
    width,
    bodyWidth,
    bodyStyle,
    className,
    popupClassName,
    buttonStyle,
    dropdownInPopup,
    invalid,
    validationError,
    disabled,
    maxVisible,
    id,
    innerButtonStyles,
    showTooltip,
    dataCy,
    isLoading,
    searchPlaceholder,
    ariaLable,
    required,
    withClearItem,
    variant,
  };
};

export const useDropdownWithCheckboxHooks = (
  props: IDropdownWithCheckboxes,
  withButtons?: boolean,
) => {
  const {
    items,
    getSelectedItems,
    title,
    width,
    containerWidth,
    id,
    placeholder,
    withFilter,
    maxVisible,
    hasError,
  } = props;
  const [focusedIndex, setFocusedIndex] = useState({
    idx: -1,
    avoidScroll: false,
  });
  const [isOpen, setIsOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropdownFocused, setDropdownFocused] = useState(false);

  const closeDropdown = useCallback(
    (focus = false) => {
      setFocusedIndex({ idx: -1, avoidScroll: false });
      setIsOpen(false);
      if (focus && btnRef.current) {
        btnRef.current.focus();
      }
    },
    [btnRef.current, setIsOpen],
  );

  const setItems = useCallback(
    (newOption?: any, shouldClose = false) => {
      if (newOption) {
        getSelectedItems(newOption);
      }
      if (shouldClose) {
        closeDropdown(true);
      }
    },
    [getSelectedItems, closeDropdown],
  );
  useScroll(
    isOpen,
    focusedIndex.idx,
    container,
    focusedIndex.avoidScroll,
    withButtons,
  );

  return {
    focusedIndex,
    setFocusedIndex,
    isOpen,
    setIsOpen,
    dropdownFocused,
    setDropdownFocused,
    setItems,
    closeDropdown,
    container,
    btnRef,
    getSelectedItems,
    items,
    id,
    title,
    width,
    containerWidth,
    placeholder,
    withFilter,
    maxVisible,
    hasError,
  };
};
